import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { CopyButton } from "@/components/Button/CopyButton";
import { InfoBox } from "@/components/InfoBox";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { maskApiKey } from "@/pages/applications/pages/apiKeys/utils/maskApiKey";

interface RevealOnceKeyProps {
  /** The full plaintext key, as the create response returned it. */
  apiKeyValue: string;
}

const styles = {
  keyRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    border: `1px solid ${theme.colors.neutral030}`,
    borderRadius: "4px",
    backgroundColor: theme.colors.staticBgPlain,
  },
  keyValue: {
    flex: 1,
    minWidth: 0,
    fontFamily: "monospace",
    fontSize: "14px",
    lineHeight: 1.43,
    color: theme.colors.staticFgTitle,
    overflowWrap: "anywhere",
  },
  toggleButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    padding: "6px",
    border: "none",
    borderRadius: "4px",
    background: "none",
    cursor: "pointer",
    color: theme.colors.staticFgNote,
    fontSize: "16px",
    "&:hover": {
      backgroundColor: theme.colors.neutral020,
    },
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

/**
 * The one place the full key is meant to be readable.
 *
 * It starts masked even here. Creating a key is often done with someone
 * looking over a shoulder or sharing a screen, and the value only has to be
 * on screen for whoever is about to store it -- copying does not need it
 * rendered, because `CopyButton` takes the value as a prop and puts it on the
 * clipboard without it ever entering the page. Revealing is one click for the
 * reader who does want to read it out.
 *
 * "Once" is not enforced here: it comes from the create response being the
 * only carrier of a freshly issued key the list will not mask, and from the
 * modal dropping that value when it closes. This component's job is to say so
 * clearly enough that the reader copies it before closing.
 */
export const RevealOnceKey: React.FC<RevealOnceKeyProps> =
  function RevealOnceKey({ apiKeyValue }) {
    const { t } = useTranslation();
    const [revealed, setRevealed] = useState(false);

    const handleToggle = useHandler(() => {
      setRevealed((current) => !current);
    });

    return (
      <Stack gap="8px">
        <InfoBox
          variant="warning"
          title={t(
            "apiKey.revealOnce.warning.title",
            "Copy this key now. It will not be shown again.",
          )}
          description={t(
            "apiKey.revealOnce.warning.description",
            "Store it somewhere safe. Once this window is closed the full key cannot be retrieved, and a replacement means issuing a new key.",
          )}
        />
        <Typography
          variant="body"
          fontWeight={500}
          color={theme.colors.staticFgBody}
        >
          {t("apiKey.revealOnce.label", "API key")}
        </Typography>
        <Box sx={styles.keyRow}>
          <Box sx={styles.keyValue} data-test="reveal-once-value">
            {revealed ? apiKeyValue : maskApiKey(apiKeyValue)}
          </Box>
          <Box
            component="button"
            type="button"
            sx={styles.toggleButton}
            data-test="reveal-once-toggle"
            aria-label={
              revealed
                ? t("apiKey.revealOnce.hide", "Hide key")
                : t("apiKey.revealOnce.show", "Show key")
            }
            onClick={handleToggle}
          >
            <MotifIcon un-i-motif={revealed ? "eye_hide" : "eye_show"} />
          </Box>
          <CopyButton variant="secondary" text={apiKeyValue} />
        </Box>
      </Stack>
    );
  };
